import { useState, type ReactNode } from 'react';
import { ScrollText } from 'lucide-react';
import { Button, Card, EmptyState, PageHeader, Select, TableSkeleton } from '@/components/ui';
import { ErrorState } from '@/components/ErrorState';
import { AuditDrawer } from '@/components/audit/AuditDrawer';
import { useAuditEvents } from '@/hooks/useAudit';
import { useSystemSettings } from '@/hooks/useSystem';
import type { AuditEventView } from '@/types/api';
import { formatDateTime, formatRelative } from '@/utils/format';
import { auditActionLabel, AUDIT_ENTITY_LABELS, label } from '@/utils/labels';

/**
 * The audit trail.
 *
 * Read-only by construction: the table is append-only in the database, and
 * nothing on this screen could change a record even if the server allowed it.
 * The page exists to answer "who did this, and when", usually after something
 * has already gone wrong — so the newest events come first and nothing is
 * summarised away.
 */

const FAMILIES = [
  { value: '', label: 'Все действия' },
  { value: 'auth.', label: 'Вход и выход' },
  { value: 'mfa.', label: 'Второй фактор' },
  { value: 'user.', label: 'Сотрудники' },
  { value: 'site.', label: 'Сайты' },
  { value: 'tag.', label: 'Теги' },
  { value: 'template.', label: 'Шаблоны' },
  { value: 'node.', label: 'Серверы' },
  { value: 'domain.', label: 'Домены' },
];

const RESULTS = [
  { value: '', label: 'Любой исход' },
  { value: 'success', label: 'Успешно' },
  { value: 'failure', label: 'Отказ' },
];

export function AuditPage(): ReactNode {
  const [family, setFamily] = useState('');
  const [result, setResult] = useState('');
  const [selected, setSelected] = useState<AuditEventView | null>(null);
  const settings = useSystemSettings();
  const events = useAuditEvents(family, result);

  const rows = events.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <div className="page">
      <PageHeader
        title="Журнал"
        subtitle={`Действия пользователей и системы. Записи не редактируются${settings.data ? `; срок хранения — ${settings.data.auditRetentionDays} дн.` : ''}`}
      />

      <div className="toolbar">
        <Select label="Действия" size="sm" value={family} onChange={setFamily} options={FAMILIES} />
        <Select label="Исход" size="sm" value={result} onChange={setResult} options={RESULTS} />
      </div>

      <Card flush>
        {events.isPending ? (
          <TableSkeleton rows={8} columns={5} />
        ) : events.isError ? (
          <ErrorState error={events.error} />
        ) : rows.length === 0 ? (
          <EmptyState icon={ScrollText} message="Нет записей по заданным условиям" />
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th scope="col">Когда</th>
                  <th scope="col">Кто</th>
                  <th scope="col">Действие</th>
                  <th scope="col">Объект</th>
                  <th scope="col">Исход</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((event) => (
                  <tr
                    key={event.id}
                    className="row-link"
                    tabIndex={0}
                    onClick={() => setSelected(event)}
                    onKeyDown={(keyboard) => {
                      if (keyboard.key === 'Enter') setSelected(event);
                    }}
                  >
                    <td className="muted nowrap" title={formatDateTime(event.createdAt)}>
                      {formatRelative(event.createdAt)}
                    </td>
                    <td className="nowrap">
                      {/* "система" rather than a blank: an action with no
                          actor was taken by the panel itself, or by someone who
                          had not proved who they were yet. */}
                      {event.actorLabel ?? <span className="faint">система</span>}
                    </td>
                    <td title={event.action}>{auditActionLabel(event.action)}</td>
                    <td className="muted nowrap">{label(AUDIT_ENTITY_LABELS, event.entityType)}</td>
                    <td>
                      <span className={`status ${event.result === 'success' ? 'status-healthy' : 'status-down'}`}>
                        <span className="status-glyph" aria-hidden="true">
                          {event.result === 'success' ? '●' : '■'}
                        </span>
                        {event.result === 'success' ? 'успешно' : 'отказ'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {events.hasNextPage ? (
        <div className="row-between">
          <span className="muted text-sm">Показано {rows.length}</span>
          <Button busy={events.isFetchingNextPage} onClick={() => void events.fetchNextPage()}>
            Показать ещё
          </Button>
        </div>
      ) : null}

      <AuditDrawer event={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
